import React from 'react';
import { cn } from '@reflex/ui/lib/utils';
import {
  ChevronRight,
  Cpu,
  Terminal,
  Shield,
  Zap,
  FolderOpen,
  Wrench,
  Puzzle,
  Server,
  Satellite,
  Gamepad2,
  Bot,
  Settings2,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { DividerBubble } from '@reflex/ui/components/stream/DividerBubble';
import { CapabilityBadges } from './shared.js';
import { initWorkingDir } from '../working-dir.js';
import type {
  SystemInitBlock,
  ClaudeInitExtensions,
  ACPInitExtensions,
  CodexInitExtensions,
} from '../types.js';

function InitInfoRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="ml-auto text-xs font-mono text-foreground/80 truncate text-right min-w-0">
        {value}
      </span>
    </div>
  );
}

const COLLAPSE_THRESHOLD = 6;

function TagBadge({ item, isActive }: { item: string; isActive: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-1.5 py-0.5 font-mono text-xs',
        isActive
          ? 'border-status-accent-violet/40 bg-status-accent-violet/10 text-status-accent-violet'
          : 'border-border/60 bg-muted/40 text-foreground/70',
      )}
    >
      {item}
    </span>
  );
}

function InitTagList({
  label,
  items,
  icon,
  highlightItem,
}: {
  label: string;
  items: string[];
  icon: React.ReactNode;
  highlightItem?: string;
}) {
  const [expanded, setExpanded] = React.useState(false);
  if (items.length === 0) return null;

  const needsCollapse = items.length > COLLAPSE_THRESHOLD;
  const collapsed = needsCollapse && !expanded;
  const visible = collapsed ? items.slice(0, COLLAPSE_THRESHOLD) : items;
  const hiddenCount = items.length - COLLAPSE_THRESHOLD;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="text-muted-foreground shrink-0">{icon}</span>
        {label}
        <Badge
          variant="secondary"
          className="ml-auto h-4 min-w-5 justify-center rounded px-1 py-0 text-xs"
        >
          {items.length}
        </Badge>
      </div>
      <div
        className={cn(
          'flex flex-wrap gap-1',
          expanded &&
            needsCollapse &&
            'max-h-40 overflow-y-auto rounded-md border border-border/30 p-1.5',
        )}
      >
        {visible.map((item) => (
          <TagBadge
            key={item}
            item={item}
            isActive={highlightItem != null && item === highlightItem}
          />
        ))}
      </div>
      {needsCollapse && (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setExpanded((v) => !v)}
          className="h-auto p-0 font-normal text-muted-foreground hover:bg-transparent hover:text-foreground/80 dark:hover:bg-transparent"
        >
          {expanded ? '↑ show less' : `+${hiddenCount} more…`}
        </Button>
      )}
    </div>
  );
}

function ClaudeInitSections({ ext }: { ext: ClaudeInitExtensions }) {
  return (
    <>
      <InitTagList icon={<Wrench className="h-3 w-3" />} label="Tools" items={ext.tools} />
      <InitTagList icon={<Zap className="h-3 w-3" />} label="Skills" items={ext.skills} />
      <InitTagList icon={<Puzzle className="h-3 w-3" />} label="Agents" items={ext.agents} />
      {ext.mcpServers.length > 0 && (
        <InitTagList
          icon={<Server className="h-3 w-3" />}
          label="MCP servers"
          items={ext.mcpServers.map((s) => (typeof s === 'string' ? s : s.name))}
        />
      )}
    </>
  );
}

function ACPInitSections({ ext }: { ext: ACPInitExtensions }) {
  const nonCategoryConfigs = ext.configOptions?.filter(
    (o) => o.category !== 'model' && o.category !== 'mode',
  );

  return (
    <>
      {ext.modes && ext.modes.length > 0 && (
        <InitTagList
          icon={<Gamepad2 className="h-3 w-3" />}
          label="Modes"
          items={ext.modes.map((m) => m.name ?? m.id)}
          highlightItem={ext.currentModeId ?? undefined}
        />
      )}
      {ext.models && ext.models.length > 0 && (
        <InitTagList
          icon={<Bot className="h-3 w-3" />}
          label="Models"
          items={ext.models.map((m) => m.name ?? m.modelId)}
          highlightItem={
            ext.currentModelId
              ? (ext.models.find((m) => m.modelId === ext.currentModelId)?.name ??
                ext.currentModelId)
              : undefined
          }
        />
      )}
      {nonCategoryConfigs && nonCategoryConfigs.length > 0 && (
        <InitTagList
          icon={<Settings2 className="h-3 w-3" />}
          label="Config"
          items={nonCategoryConfigs.map((o) => `${o.name ?? o.id}: ${o.currentValue ?? '-'}`)}
        />
      )}
      {ext.agentCapabilities && typeof ext.agentCapabilities === 'object' && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Wrench className="h-3 w-3 shrink-0" />
            Agent Capabilities
          </div>
          <CapabilityBadges caps={ext.agentCapabilities as Record<string, unknown>} />
        </div>
      )}
    </>
  );
}

function CodexInitRows({ ext }: { ext: CodexInitExtensions }) {
  return (
    <>
      {ext.reasoningEffort && (
        <InitInfoRow
          icon={<Zap className="h-3 w-3" />}
          label="Effort"
          value={ext.reasoningEffort}
        />
      )}
      {ext.approvalPolicy && (
        <InitInfoRow
          icon={<Shield className="h-3 w-3" />}
          label="Approvals"
          value={ext.approvalPolicy}
        />
      )}
      {ext.sandboxMode && (
        <InitInfoRow
          icon={<Server className="h-3 w-3" />}
          label="Sandbox"
          value={ext.sandboxMode}
        />
      )}
      {ext.modelProvider && (
        <InitInfoRow
          icon={<Satellite className="h-3 w-3" />}
          label="Provider"
          value={ext.modelProvider}
        />
      )}
    </>
  );
}

export function SystemInitPopoverContent({
  block,
  heading = 'Session initialized',
}: {
  block: SystemInitBlock;
  heading?: 'Session initialized' | 'Session details';
}) {
  const ext = block.extensions;
  const claude = ext?.protocol === 'claude' ? ext : null;
  const acp = ext?.protocol === 'acp' ? ext : null;
  const codex = ext?.protocol === 'codex' ? ext : null;

  const resolvedModelName =
    acp?.currentModelId && acp.models
      ? (acp.models.find((m) => m.modelId === acp.currentModelId)?.name ?? block.model)
      : block.model;
  const modelLabel = resolvedModelName ?? 'unknown';
  const version = claude?.claudeCodeVersion ?? block.agentVersion;
  // Same read the transcript's paths resolve against, so the popover and the
  // file previews can never name different directories.
  const workingDir = initWorkingDir(block);

  return (
    <>
      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/20 px-3 py-2.5">
        <div className="rounded-full bg-status-accent-violet/20 p-1">
          <Cpu className="h-3.5 w-3.5 text-status-accent-violet" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-foreground/90">{heading}</p>
          {(claude?.sessionId ?? acp?.sessionId ?? codex?.sessionId) && (
            <p className="text-xs text-muted-foreground font-mono truncate">
              {claude?.sessionId ?? acp?.sessionId ?? codex?.sessionId}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-0.5 border-b border-border/60 px-3 py-2">
        <InitInfoRow icon={<Cpu className="h-3 w-3" />} label="Model" value={modelLabel} />
        {version && (
          <InitInfoRow
            icon={<Terminal className="h-3 w-3" />}
            label="Version"
            value={`v${version}`}
          />
        )}
        {workingDir && (
          <InitInfoRow
            icon={<FolderOpen className="h-3 w-3" />}
            label="Working dir"
            value={workingDir}
          />
        )}
        {codex && <CodexInitRows ext={codex} />}
        {claude?.permissionMode && (
          <InitInfoRow
            icon={<Shield className="h-3 w-3" />}
            label="Permissions"
            value={claude.permissionMode}
          />
        )}
        {claude?.apiKeySource && (
          <InitInfoRow
            icon={<Zap className="h-3 w-3" />}
            label="API key"
            value={claude.apiKeySource}
          />
        )}
        {claude?.fastModeState && (
          <InitInfoRow
            icon={<Zap className="h-3 w-3" />}
            label="Fast mode"
            value={claude.fastModeState}
          />
        )}
        {acp !== null && acp.protocolVersion != null && (
          <InitInfoRow
            icon={<Satellite className="h-3 w-3" />}
            label="Protocol"
            value={`ACP v${acp.protocolVersion}`}
          />
        )}
        {acp?.currentModeId && (
          <InitInfoRow
            icon={<Gamepad2 className="h-3 w-3" />}
            label="Mode"
            value={acp.currentModeId}
          />
        )}
      </div>

      <div className="space-y-3 px-3 py-2.5">
        {claude && <ClaudeInitSections ext={claude} />}
        {acp && <ACPInitSections ext={acp} />}
        {block.commands.length > 0 && (
          <InitTagList
            icon={<Terminal className="h-3 w-3" />}
            label="Commands"
            items={block.commands}
          />
        )}
      </div>

      {claude && (claude.tools.length > 0 || claude.mcpServers.length > 0) && (
        <div className="border-t border-border/60 bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
          {claude.tools.length} tools · {claude.skills.length} skills · {claude.agents.length}{' '}
          agents · {claude.mcpServers.length} MCP servers
        </div>
      )}
      {acp && (
        <div className="border-t border-border/60 bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
          {acp.modes?.length ?? 0} modes · {acp.models?.length ?? 0} models ·{' '}
          {acp.configOptions?.length ?? 0} config options
        </div>
      )}
    </>
  );
}

export function SystemInitBlockView({ block }: { block: SystemInitBlock }) {
  const [hasOpened, setHasOpened] = React.useState(false);
  const ext = block.extensions;
  const claude = ext?.protocol === 'claude' ? ext : null;
  const acp = ext?.protocol === 'acp' ? ext : null;

  const resolvedModelName =
    acp?.currentModelId && acp.models
      ? (acp.models.find((m) => m.modelId === acp.currentModelId)?.name ?? block.model)
      : block.model;
  const modelLabel = resolvedModelName ?? 'unknown';
  const version = claude?.claudeCodeVersion ?? block.agentVersion;

  return (
    <DividerBubble event={block.sourceEvent} className="my-1.5 py-1.5">
      <Popover
        onOpenChange={(open: boolean) => {
          if (open) setHasOpened(true);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              'h-auto gap-1.5 rounded-full border border-border/60 bg-muted/30 px-2.5 py-1',
              'text-xs font-normal text-muted-foreground',
              'hover:border-border hover:bg-muted/50 hover:text-foreground/80 dark:hover:bg-muted/50',
            )}
          >
            <Cpu className="size-3 text-status-accent-violet" />
            <span className="font-medium">{modelLabel}</span>
            {version && <span className="text-muted-foreground">v{version}</span>}
            {acp !== null && acp.protocolVersion != null && (
              <span className="text-muted-foreground">ACP v{acp.protocolVersion}</span>
            )}
            <ChevronRight className="size-3 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        {hasOpened && (
          <PopoverContent
            side="bottom"
            align="center"
            className="w-[340px] max-h-[min(480px,70vh)] overflow-y-auto p-0"
          >
            <SystemInitPopoverContent block={block} />
          </PopoverContent>
        )}
      </Popover>
    </DividerBubble>
  );
}
